/**
 * Minimal challenge shape the minigame demos need. Both the library `Challenge`
 * and the `EventChallenge` types satisfy it, so one set of demo components
 * serves both detail pages.
 */
export interface DemoChallenge {
  id: string;
  type: string;
  /** Optional — event challenges may omit it; puzzle is also keyed on `type`. */
  answerType?: string;
  isTimed?: boolean;
  imageUrl?: string | null;
  puzzleGridSize?: number | null;
  minigameConfig?: Record<string, unknown> | null;
}

// --- defensive parsers (minigameConfig is `Record<string, unknown>`) ---------

type Cfg = Record<string, unknown> | null | undefined;

function strArray(value: unknown): string[] {
  return Array.isArray(value) ? value.filter((v): v is string => typeof v === 'string') : [];
}

function numberOr(value: unknown, fallback: number): number {
  return typeof value === 'number' && Number.isFinite(value) ? value : fallback;
}

function stringOr(value: unknown, fallback = ''): string {
  return typeof value === 'string' ? value : fallback;
}

export interface MemoryMatchConfig {
  cardImages: string[];
  gridSize: number;
  timeLimit: number;
}
export function parseMemoryMatch(c: Cfg): MemoryMatchConfig {
  const cfg = c ?? {};
  return {
    cardImages: strArray(cfg.cardImages),
    gridSize: numberOr(cfg.gridSize, 4),
    timeLimit: numberOr(cfg.timeLimit, 60),
  };
}

export interface SlotMachineConfig {
  reelImages: string[];
  reelCount: number;
  winCondition: number;
  pointsOnWin: number;
  pointsOnLoss: number;
}
export function parseSlotMachine(c: Cfg): SlotMachineConfig {
  const cfg = c ?? {};
  return {
    reelImages: strArray(cfg.reelImages),
    reelCount: numberOr(cfg.reelCount, 5),
    winCondition: numberOr(cfg.winCondition, 4),
    pointsOnWin: numberOr(cfg.pointsOnWin, 100),
    pointsOnLoss: numberOr(cfg.pointsOnLoss, 0),
  };
}

export interface GuessWordConfig {
  word: string;
  category: string;
  maxGuesses: number;
}
export function parseGuessWord(c: Cfg): GuessWordConfig {
  const cfg = c ?? {};
  return {
    word: stringOr(cfg.word).toUpperCase(),
    category: stringOr(cfg.category),
    maxGuesses: numberOr(cfg.maxGuesses, 6),
  };
}

export interface GuessSongConfig {
  audioUrl: string;
  songTitle: string;
  artist: string;
  playDuration: number;
}
export function parseGuessSong(c: Cfg): GuessSongConfig {
  const cfg = c ?? {};
  return {
    audioUrl: stringOr(cfg.audioUrl),
    songTitle: stringOr(cfg.songTitle),
    artist: stringOr(cfg.artist),
    playDuration: numberOr(cfg.playDuration, 15),
  };
}

export interface RelatedWordsConfig {
  theme: string;
  words: string[];
  decoyWords: string[];
}
export function parseRelatedWords(c: Cfg): RelatedWordsConfig {
  const cfg = c ?? {};
  return {
    theme: stringOr(cfg.theme),
    words: strArray(cfg.words),
    decoyWords: strArray(cfg.decoyWords),
  };
}
